import { html, repeat, when, ref } from '@microsoft/fast-element';
import type { VppaForm } from './vppa-form';
import { sync } from '@genesislcap/foundation-utils';
import { roundNumber } from '../../../../utils/formatting';
import { getEnumArrayWithoutNotApplicableValue } from '../../../../utils';
import { CONTRACT_TYPE, PRODUCT_TYPE, VPPA_RFQ_STATUS } from '../../../../utils';

export const VppaFormTemplate = html<VppaForm>`
<form>
    <div class="auto-exec-message" style="display: ${(x) => (x.contractTypeValue === x.autoExecString && !x.isBusinessHours ? '' : 'none')}">
        Auto-Execute Contracts may only be listed during Platform trading hours, 9:00 ET to 17:00 ET.
    </div>
    <div class="form-control required">
        <label>Project</label>
        <zero-select
            ${ref('projectElement')}
            :value=${sync((x) => x.projectValue)}
            class="input-control"
            @change="${(x) => x.projectSelected()}"
            ?disabled=${(x) => x.isEditModal}
        >
        <zero-option value="">Select Project</zero-option>
        ${repeat(
          (x) => x.projectList,
          html`
            <zero-option value="${(x) => x.PROJECT_ID}">${(x) => x.NAME}</zero-option>
          `,
        )}
        </zero-select>
    </div>

    <div class="form-control" ?hidden=${(x) =>
      (x.vppaStatusValue != VPPA_RFQ_STATUS.ACTIVE &&
        x.vppaStatusValue != VPPA_RFQ_STATUS.DELISTED) ||
      !x.isEditModal}>
       <label>Status</label>
        <zero-select
            ${ref('vppaStatusElement')}
            :value=${sync((x) => x.vppaStatusValue)}
            class="input-control"
        >
            ${repeat(
              (x) => x.vppaStatuses,
              html`
                <zero-option
                  ?hidden="${(x) =>
                    x.ENUM_VALUE != VPPA_RFQ_STATUS.ACTIVE &&
                    x.ENUM_VALUE != VPPA_RFQ_STATUS.DELISTED}"
                  value="${(x) => x.ENUM_VALUE}"
                >
                  ${(x) => x.READABLE_VALUE}
                </zero-option>
              `,
            )}
        </zero-select>
    </div>
    
    <div class="form-control required">
    <label>Product Type</label>
        <zero-select
            ${ref('productTypeElement')}
            :value=${sync((x) => x.productTypeValue)}
            class="input-control"
            @change=${(x) => x.loadRequiredFields()}
            ?disabled=${(x) => x.isEditModal}
        >
        <zero-option value="">Select Product Type</zero-option>
            ${repeat(
              (x) => x.productTypesDisplayed,
              html`
                <zero-option value="${(x) => x.ENUM_VALUE}">${(x) => x.READABLE_VALUE}</zero-option>
              `,
            )}
        </zero-select>
    </div>

 
    <div class="form-control required">
        <label>Contract Type</label>
        <zero-select 
            ${ref('contractTypeElement')}
            :value=${sync((x) => x.contractTypeValue)}
            class="input-control"
            ?disabled=${(x) =>
              x.isEditModal || x.contractTypeValue === CONTRACT_TYPE.REC || !x.productTypeValue}

        >
            <zero-option value="">Select Contract Type</zero-option>
            ${repeat(
              (x) => x.contractTypesDisplayed,
              html`
                <zero-option value="${(x) => x.ENUM_VALUE}">${(x) => x.READABLE_VALUE}</zero-option>
              `,
            )}
        </zero-select>
    </div>

    <div class="form-control required">
        <label>Contract Legal Entity</label>
        <zero-select 
            ${ref('legalEntityElement')}
            :value=${sync((x) => x.legalEntityValue)}
            class="input-control"
            ?disabled=${(x) => x.isEditModal}
        >
        <zero-option value="">Select Project or Company Name</zero-option>
        ${repeat(
          (x) => x.vppaLegalEntityList,
          html`
            <zero-option value="${(x) => x}">${(x) => x}</zero-option>
          `,
        )}
        </zero-select>
    </div>

    <zero-text-field
        ${ref('vppaRefElement')}
        class="input-control"
        type="text"
        placeholder="Enter..."
        maxlength="64"
        style="display: ${(x) => (x.contractTypeValue === x.autoExecString ? 'none' : '')}"
        :value=${sync((x) => x.vppaRefValue)}
    >Contract Reference <info-icon id="contractReferenceTooltip"></info-icon></zero-text-field>
    <zero-tooltip anchor="contractReferenceTooltip" position="top">
        Add an internal note specific to this Contract. Contract Reference is not visible to counterparties.
    </zero-tooltip>

    <zero-text-field 
        ${ref('projectIsoElement')}
        class="input-control" 
        type="text" 
        placeholder="Enter..."
        maxlength="64"
        required
        disabled
        :value=${sync((x) => x.projectIso)}
    >ISO</zero-text-field>

    <div class="form-control" 
        style="display: ${(x) => (x.contractTypeValue !== x.autoExecString ? 'none' : '')}">
      <label class="form-label">Technology Type</label>
      <zero-segmented-control
        disabled="true"
        ${ref('techTypeElement')}
        appearance="primary"
        orientation="horizontal"
        :value="${sync((x) => x.techTypeValue)}"
      >
        <zero-segmented-item value="WIND">
          Wind
        </zero-segmented-item>
        <zero-segmented-item value="SOLAR">
          Solar
        </zero-segmented-item>
      </zero-segmented-control>
    </div>
    
    <div class="form-control required"  style="${(x) =>
      x.productTypeValue === PRODUCT_TYPE.RECS ? 'display: none' : ''}">
        <label>Settlement Point</label>
        <zero-select 
            ${ref('settlementPointElement')}
            ?disabled=${(x) => x._settlementPoint.length === 0 || x.isEditModal}
            class="input-control"
            :value=${sync((x) => x.settlementPointValue)}
            @change="${(x) => x.settlementPointSelected()}"
        >
        <zero-option value="">Select Settlement Point</zero-option>
        ${repeat(
          (x) => x._settlementPoint,
          html`
            <zero-option value="${(x) => x}">${(x) => x}</zero-option>
          `,
        )}
        </zero-select>
    </div>

    <div class="form-control required"  style="${(x) =>
      x.productTypeValue === PRODUCT_TYPE.RECS ? 'display: none' : ''}">
        <label>Settlement Point Index</label>
        <zero-select 
            ${ref('settlementIndexElement')}
            ?disabled=${(x) => x._settlementIndex.length === 0}
            :value=${sync((x) => x.settlementIndexValue)}
            class="input-control"
            @change="${(x) => x.settlementIndexSelected()}"
        >
        <zero-option value="">Select Settlement Point Index</zero-option>
        ${repeat(
          (x) => x._settlementIndex,
          html`
            <zero-option value="${(x) => x}">${(x) => x}</zero-option>
          `,
        )}
        </zero-select>
    </div>

    <zero-number-field 
        ${ref('fixedPriceElement')}
        class="input-control"
        placeholder="Enter..." 
        min="0"
        hide-step
        withFormatting
        @blur=${(x) =>
          (x.fixedPriceValue = parseFloat(x.fixedPriceValue)
            ? roundNumber(parseFloat(x.fixedPriceValue)).toString()
            : '')}
        required
        :value=${sync((x) => x.fixedPriceValue)}
    >Fixed Price</zero-number-field>
    
    <div class="form-control date long-error required">
      <label>Tenor Start Date</label>
      <zero-text-field 
          placeholder="Enter..."  
          ${ref('tenorStartDateElement')}
          type="date" 
          onkeydown="return false"
          :value=${sync((x) => x.tenorStartDateValue)}
          @change=${(x) => x.validateTenorStartDateValue()}
      ></zero-text-field>
    </div>
    
    <zero-number-field
            ${ref('tenorMonthElement')}
            class="input-control"
            placeholder="Enter..."
            min="1"
            max="240"
            hide-step
            required
            @blur=${(x) =>
              (x.tenorMonthValue = parseInt(x.tenorMonthValue)
                ? parseInt(x.tenorMonthValue).toString()
                : '')}
            :value=${sync((x) => x.tenorMonthValue)}
    >Tenor (Months) <info-icon id="tenorMonthTooltip"></info-icon></zero-number-field>
    <zero-tooltip anchor="tenorMonthTooltip" position="top">
        Tenor months is a whole number in range from 1 to 240
    </zero-tooltip>

    <zero-number-field
        class="input-control"
        type="number"
        readonly
        disabled
        :value=${sync((x) => x.megawattAc)}
    >Nameplate Capacity (MW<sub>AC</sub>)
        <info-icon id="nameplateCapacityTooltip"></info-icon>
    </zero-number-field>

    <zero-tooltip anchor="nameplateCapacityTooltip" position="top">
        The maximum rated output of the energy generation facility, expressed in megawatts AC
        (MW
        <sub>AC</sub>
        )
    </zero-tooltip>

    <zero-number-field 
        ${ref('offeredCapacityElement')}
        class="input-control"
        id="offeredCapacity"
        readonly
        disabled
        withFormatting
        :value=${sync((x) => x.offeredCapacityValue)}
    >${(x) => x.getBuyersCapacityLabel()}
        <info-icon id="offeredCapacityTooltip"></info-icon>
    </zero-number-field>
    <zero-tooltip anchor="offeredCapacityTooltip" position="top">
    The maximum rated output of the portion of the generator that the buyer is purchasing, expressed in megawatts AC (MW<sub>AC</sub>)
    </zero-tooltip>

    <zero-number-field
        ${ref('buyersFractionElement')}
        id="fractionValue"
        class="input-control"
        type="number"
        @blur=${(x) => x.roundBuyersFractional()}
        placeholder="Enter..."
        min="0"
        required
        hide-step
        :value=${sync((x) => x.buyersFractionValue)}
        @change=${(x) => x.validateBuyerFraction()}
    >Buyer's Fraction  <info-icon id="fractionValueTooltip"></info-icon> </zero-number-field>
    <zero-tooltip anchor="fractionValueTooltip" position="top">
    The portion of the nameplate capacity that is being sold to the buyer, expressed as a percentage (%)
    </zero-tooltip>

    <zero-number-field
        class="form-control"
        style="display: ${(x) => (x.productTypeValue === PRODUCT_TYPE.RECS ? '' : 'none')}"
        ${ref('floorQuantityElement')}
        type="number"
        placeholder="Enter..."
        :value=${sync((x) => x.floorQuantityValue)}
        hide-step
      >
      Floor Quantity
      </zero-number-field>

    <div
        class="form-control required"
        style="display: ${(x) => (x.contractTypeValue === x.autoExecString ? 'none' : '')}"
      >
        <label>Credit Profile</label>
        <zero-select
          ${ref('creditProfileElement')}
          class="input-control"
          :value=${sync((x) => x.creditProfileValue)}
          @change="${(x) => x.creditProfileSelected()}"
        >
        <zero-option value="">Select Credit Profile</zero-option>

        ${repeat(
          (x) => x.creditProfiles,
          html`
            <zero-option value="${(x) => x.ENUM_VALUE}">${(x) => x.READABLE_VALUE}</zero-option>
          `,
        )}
        </zero-select>
    </div>

    <div class="form-control" 
        style="display: ${(x) => (x.contractTypeValue === x.autoExecString ? 'none' : '')}">
        <zero-number-field 
          ${ref('sellerPostCodCollateralElement')}
          class="input-control"
          type="number"
          withFormatting
          min="0"
          :disabled=${(x) => x.creditProfileValue != 'LETTER_OF_CREDIT'}
          placeholder=${(x) => x.sellerPostCodCollateralPlaceholder}
          min="0"
          required
          hide-step
          :value=${sync((x) => x.sellerPostCodCollateralValue)}
        >
          Seller Post-COD Credit Support (Max)
        </zero-number-field >
    </div>

    <div class="form-control required"
        style="display: ${(x) =>
          x.contractTypeValue === x.autoExecString || x.productTypeValue === PRODUCT_TYPE.RECS
            ? 'none'
            : ''}">
        <label>Negative Pricing Method</label>
        <zero-select 
            ${ref('negativePriceMethodElement')}
            class="input-control"
            :value=${sync((x) => x.negativePriceMethodValue)}
        >
        <zero-option value="">Select Negative Pricing Method</zero-option>
        ${repeat(
          (x) => getEnumArrayWithoutNotApplicableValue(x.negativePricingMethod),
          html`
            <zero-option value="${(x) => x.ENUM_VALUE}">${(x) => x.READABLE_VALUE}</zero-option>
          `,
        )}
        </zero-select>
        </zero-select>
    </div>

    <div
        class="form-control required"
        style="display: ${(x) => (x.contractTypeValue === x.autoExecString ? 'none' : '')}"
      >
        <label>Applicable REC Program</label>
        <zero-select
          ${ref('applicableRECProgramElement')}
          class="input-control"
          :value=${sync((x) => x.applicableRECProgramValue)}
          @change="${(x) => x.applicableRECProgramSelected()}"
        >
        <zero-option value="">Select Applicable REC Program</zero-option>

        ${repeat(
          (x) => x.applicableRECProgram,
          html`
            <zero-option value="${(x) => x.ENUM_VALUE}">${(x) => x.READABLE_VALUE}</zero-option>
          `,
        )}
        </zero-select>
      </div>

     <div class="form-control ${(x) => (x.applicableRECProgramValue != 'NONE' ? 'required' : '')}"
        style="display: ${(x) => (x.contractTypeValue === x.autoExecString ? 'none' : '')}"
        >
         <label>Applicable Tracking System</label>
         <zero-select
           ${ref('applicableTrackingSystem')}
           class="input-control"
           :value=${sync((x) => x.applicableTrackingSystemValue)}
           ?disabled=${(x) => !x.applicableRECProgramValue || x.applicableRECProgramValue == 'NONE'}
         >
           <zero-option value="">Select Applicable Tracking System</zero-option>
           ${repeat(
             (x) => getEnumArrayWithoutNotApplicableValue(x.applicableTrackingSystemEnum),
             html`
               <zero-option value="${(x) => x.ENUM_VALUE}">${(x) => x.READABLE_VALUE}</zero-option>
             `,
           )}
         </zero-select>
       </div>
            
    <div class="form-control ${(x) => (x.applicableRECProgramValue != 'NONE' ? 'required' : '')}"
        style="display: ${(x) => (x.contractTypeValue === x.autoExecString ? 'none' : '')}">
        <label>REC Delivery Methodology</label>
        <zero-select 
            ${ref('recDeliveryMethodologyElement')}
            class="input-control"
            :value=${sync((x) => x.recDeliveryMethodologyValue)}
            ?disabled=${(x) => !x.applicableRECProgramValue || x.applicableRECProgramValue == 'NONE'}
        >
        <zero-option value="">Select REC Delivery Methodology</zero-option>        
        ${repeat(
          (x) => getEnumArrayWithoutNotApplicableValue(x.recDeliveryMethodology),
          html`
            <zero-option value="${(x) => x.ENUM_VALUE}">${(x) => x.READABLE_VALUE}</zero-option>
          `,
        )}
        </zero-select>
    </div>
    
    <div class="form-control required"
        style="display: ${(x) => (x.contractTypeValue === x.autoExecString ? 'none' : '')}">
        <label>Performance Guarantee Method</label>
        <zero-select 
            ${ref('performanceGuaranteeMethodElement')}
            class="input-control"
            :value=${sync((x) => x.performanceGuaranteeMethodValue)}
        >
        <zero-option value="">Select Performance Guarantee Method</zero-option>        
        ${repeat(
          (x) => getEnumArrayWithoutNotApplicableValue(x.performanceGuaranteeMethod),
          html`
            <zero-option value="${(x) => x.ENUM_VALUE}">${(x) => x.READABLE_VALUE}</zero-option>
          `,
        )}
        </zero-select>
    </div>
    <div class="auto-exec-message-div">
        <span class="auto-exec-message" style="display: ${(x) =>
          x.contractTypeValue !== x.autoExecString || x.isEditModal ? 'none' : ''}">
            By clicking Add New Contract, an offer will be created for a standardized Mini-Post Build VPPA. A transaction will be automatically executed when the offer matches with a bid based on the criteria specified above.
          </span>
      </div>
       ${when(
         (x) => x.isEditModal && x.isKeyMetricsWillBeUpdated,
         html`
           <div class="auto-exec-message-div">
             <span class="auto-exec-message">
               Key metrics will be requested upon submitting this contract, and can be viewed in
               Project Metrics.
             </span>
           </div>
         `,
       )}
</form>
`;
